import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
  MenuIcon,
  SearchIcon,
  DownloadIcon,
  UploadIcon,
  DollarIcon,
  CheckIcon,
  TrendingUpIcon,
} from '../UI/Icons';
import { DEFAULT_ITEM_IMAGE, FoodMenuItem, Vendor } from '../../utils/constants';
import BulkMenuActions, { MenuImportState } from '../Forms/BulkMenuActions';
import vendorService, { CreateMenuItemInput } from '../../services/vendorService';
import toastService from '../../services/toastService';
import StatTile from '../Dashboard/StatTile';
import Modal from '../UI/Modal';
import styles from './MenuManagement.module.css';

type AvailabilityFilter = 'all' | 'available' | 'unavailable';
type SortOption = 'name-asc' | 'price-desc' | 'price-asc';

interface MenuManagementProps {
  vendor: Vendor;
  onBack: () => void;
  onAddMenuItem: () => void;
  onEditMenuItem: (item: FoodMenuItem) => void;
  onDeleteMenuItem: (itemId: string) => void;
}

const UNCATEGORIZED = 'Uncategorized';

function isItemAvailable(item: FoodMenuItem) {
  return item.available !== false && (item.available as unknown) !== 0;
}

function countModifierGroups(item: FoodMenuItem) {
  return item.modifiers?.length || 0;
}

function formatCategoryLabel(category: string): string {
  if (category === UNCATEGORIZED) return UNCATEGORIZED;
  return category.replace(/[-_]/g, ' ');
}

function getItemCategory(item: FoodMenuItem): string {
  return item.category?.trim() || UNCATEGORIZED;
}

interface MenuItemCardProps {
  item: FoodMenuItem;
  selected: boolean;
  togglingId: string | null;
  onToggleSelect: (itemId: string, selected: boolean) => void;
  onEdit: (item: FoodMenuItem) => void;
  onDelete: (item: FoodMenuItem) => void;
  onToggleAvailability: (item: FoodMenuItem) => void;
}

function MenuItemCard({
  item,
  selected,
  togglingId,
  onToggleSelect,
  onEdit,
  onDelete,
  onToggleAvailability,
}: MenuItemCardProps) {
  const isAvailable = isItemAvailable(item);
  const modifierCount = countModifierGroups(item);
  const displayPrice =
    item.is_promo && item.promo_price != null ? item.promo_price : item.price;

  return (
    <div className={`${styles.itemCard} ${selected ? styles.itemCardSelected : ''}`}>
      <div className={styles.itemSelect}>
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onToggleSelect(item.id, e.target.checked)}
          className={styles.checkbox}
          aria-label={`Select ${item.name}`}
        />
      </div>

      <div className={styles.itemImage}>
        <img
          src={item.image || DEFAULT_ITEM_IMAGE}
          alt=""
          onError={(e) => {
            (e.target as HTMLImageElement).src = DEFAULT_ITEM_IMAGE;
          }}
        />
      </div>

      <div className={styles.itemContent}>
        <div className={styles.itemHeader}>
          <h3 className={styles.itemName}>{item.name}</h3>
          <div className={styles.itemPriceBlock}>
            <span className={styles.itemPriceValue}>${displayPrice.toFixed(2)}</span>
            {item.is_promo && item.promo_price != null && (
              <span className={styles.itemOriginalPrice}>${item.price.toFixed(2)}</span>
            )}
          </div>
        </div>

        {item.description && <p className={styles.itemDescription}>{item.description}</p>}

        <div className={styles.badgeRow}>
          {item.featured && <span className={`${styles.badge} ${styles.badgeFeatured}`}>Featured</span>}
          {item.is_promo && <span className={`${styles.badge} ${styles.badgePromo}`}>Promo</span>}
          {item.category && (
            <span className={styles.badge}>{formatCategoryLabel(item.category)}</span>
          )}
          {modifierCount > 0 && (
            <span className={styles.badge}>
              {modifierCount} option group{modifierCount === 1 ? '' : 's'}
            </span>
          )}
          {item.dietaryTags?.map((tag) => (
            <span key={tag} className={`${styles.badge} ${styles.badgeDietary}`}>
              {tag}
            </span>
          ))}
          {(item.allergens?.length || 0) > 0 && (
            <span className={`${styles.badge} ${styles.badgeAllergen}`}>
              Allergens: {item.allergens!.length}
            </span>
          )}
        </div>

        <label className={styles.availabilityToggle}>
          <input
            type="checkbox"
            checked={isAvailable}
            disabled={togglingId === item.id}
            onChange={() => onToggleAvailability(item)}
          />
          <span>Available</span>
        </label>
      </div>

      <div className={styles.itemActions}>
        <button
          type="button"
          onClick={() => onEdit(item)}
          className={`${styles.itemAction} ${styles.itemActionEdit}`}
          title={`Edit ${item.name}`}
        >
          <EditIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(item)}
          className={`${styles.itemAction} ${styles.itemActionDelete}`}
          title={`Delete ${item.name}`}
        >
          <DeleteIcon size={16} />
        </button>
      </div>
    </div>
  );
}

function MenuItemCardSkeleton() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={`${styles.skeletonBlock} ${styles.skeletonThumb}`} />
      <div className={styles.skeletonBody}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonBadge}`} />
      </div>
    </div>
  );
}

const MenuManagement: React.FC<MenuManagementProps> = ({
  vendor,
  onBack,
  onAddMenuItem,
  onEditMenuItem,
  onDeleteMenuItem,
}) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [vendorData, setVendorData] = useState(vendor);
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FoodMenuItem | null>(null);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [importState, setImportState] = useState<MenuImportState>({
    previewCount: 0,
    importItems: () => {},
  });

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');

  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVendorData(vendor);
    setSelectedItems(new Set());
    setSearch('');
    setCategoryFilter('all');
    setAvailabilityFilter('all');
    setSortBy('name-asc');
    setCollapsedCategories(new Set());
  }, [vendor]);

  useEffect(() => {
    if (!actionsOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [actionsOpen]);

  const menuItems = useMemo(() => vendorData.menuItems || [], [vendorData.menuItems]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    menuItems.forEach((item) => {
      if (item.category) set.add(item.category);
    });
    return Array.from(set).sort();
  }, [menuItems]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    let list = menuItems.filter((item) => {
      const available = isItemAvailable(item);
      if (availabilityFilter === 'available' && !available) return false;
      if (availabilityFilter === 'unavailable' && available) return false;
      if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
      if (!query) return true;
      return [item.name, item.description, item.category || '']
        .join(' ')
        .toLowerCase()
        .includes(query);
    });

    list = [...list].sort((a, b) => {
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'price-asc') return a.price - b.price;
      return a.name.localeCompare(b.name);
    });

    return list;
  }, [menuItems, search, categoryFilter, availabilityFilter, sortBy]);

  const itemsByCategory = useMemo(() => {
    const map = new Map<string, FoodMenuItem[]>();
    filteredItems.forEach((item) => {
      const cat = getItemCategory(item);
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    });
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === UNCATEGORIZED) return 1;
      if (b === UNCATEGORIZED) return -1;
      return a.localeCompare(b);
    });
  }, [filteredItems]);

  const unavailableCount = menuItems.filter((item) => !isItemAvailable(item)).length;
  const availableCount = menuItems.length - unavailableCount;
  const promoCount = menuItems.filter((item) => item.is_promo).length;
  const avgPrice = useMemo(() => {
    if (menuItems.length === 0) return null;
    const total = menuItems.reduce((sum, item) => sum + item.price, 0);
    return total / menuItems.length;
  }, [menuItems]);

  const loading = vendorData.menuItems == null;
  const showSkeleton = loading || (refreshing && menuItems.length === 0);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const updated = await vendorService.getById(vendor.id);
      setVendorData(updated);
      toastService.success('Menu refreshed');
    } catch (error) {
      console.error('Refresh failed:', error);
      toastService.error('Failed to refresh menu');
    } finally {
      setRefreshing(false);
    }
  };

  const handleItemSelect = (itemId: string, selected: boolean) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (selected) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  const handleBulkAction = async (action: 'makeAvailable' | 'makeUnavailable', itemIds: string[]) => {
    setIsBulkLoading(true);
    try {
      const available = action === 'makeAvailable';
      await vendorService.bulkUpdateAvailability(vendor.id, itemIds, available);
      setVendorData((prev) => ({
        ...prev,
        menuItems: (prev.menuItems ?? []).map((mi) =>
          itemIds.includes(mi.id) ? { ...mi, available } : mi,
        ),
      }));
      toastService.success(`${itemIds.length} item${itemIds.length === 1 ? '' : 's'} updated`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Bulk operation failed:', error);
      toastService.error('Bulk update failed');
    } finally {
      setIsBulkLoading(false);
    }
  };

  const confirmBulkDelete = async () => {
    const itemIds = Array.from(selectedItems);
    if (itemIds.length === 0) return;

    setConfirmLoading(true);
    try {
      const results = await Promise.allSettled(
        itemIds.map((id) => vendorService.deleteMenuItem(vendor.id, id)),
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      const updated = await vendorService.getById(vendor.id);
      setVendorData(updated);
      setSelectedItems(new Set());
      setPendingBulkDelete(false);

      if (failed === 0) {
        toastService.success(`Deleted ${succeeded} item${succeeded === 1 ? '' : 's'}`);
      } else if (succeeded > 0) {
        toastService.warning(`Deleted ${succeeded}; ${failed} failed`);
      } else {
        toastService.error('Failed to delete selected items');
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toastService.error('Failed to delete selected items');
    } finally {
      setConfirmLoading(false);
    }
  };

  const confirmSingleDelete = async () => {
    if (!pendingDelete) return;
    setConfirmLoading(true);
    try {
      await onDeleteMenuItem(pendingDelete.id);
      setSelectedItems((prev) => {
        const next = new Set(prev);
        next.delete(pendingDelete.id);
        return next;
      });
      setPendingDelete(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleBulkImport = async (items: Record<string, string>[]) => {
    setIsBulkLoading(true);
    try {
      const menuItemsPayload: CreateMenuItemInput[] = items.map((item) => ({
        name: item.name || '',
        price: parseFloat(item.price) || 0,
        description: item.description || '',
        category: item.category || '',
        image: null,
        available: true,
      }));

      const updatedVendor = await vendorService.bulkImportMenuItems(vendor.id, menuItemsPayload);
      setVendorData(updatedVendor);
      toastService.success(`Imported ${menuItemsPayload.length} menu items`);
    } catch (error) {
      console.error('Bulk import failed:', error);
      toastService.error('Failed to import menu items');
    } finally {
      setIsBulkLoading(false);
    }
  };

  const handleToggleAvailability = async (item: FoodMenuItem) => {
    const currentlyAvailable = isItemAvailable(item);
    setTogglingId(item.id);
    try {
      await vendorService.bulkUpdateAvailability(vendor.id, [item.id], !currentlyAvailable);
      setVendorData((prev) => ({
        ...prev,
        menuItems: prev.menuItems.map((mi) =>
          mi.id === item.id ? { ...mi, available: !currentlyAvailable } : mi,
        ),
      }));
      toastService.success(
        `"${item.name}" marked ${!currentlyAvailable ? 'available' : 'unavailable'}`,
      );
    } catch (error) {
      console.error('Availability toggle failed:', error);
      toastService.error('Failed to update availability');
    } finally {
      setTogglingId(null);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportMenu = async () => {
    try {
      setExporting(true);
      setActionsOpen(false);
      const blob = await vendorService.exportMenuItemsCsv(vendor.id);
      downloadBlob(blob, `menu-${vendor.name?.replace(/\s+/g, '-').toLowerCase() || 'vendor'}.csv`);
      toastService.success('Menu exported');
    } catch (error) {
      console.error('Menu export failed:', error);
      toastService.error('Failed to export menu');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('all');
    setAvailabilityFilter('all');
    setSortBy('name-asc');
  };

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const hasFilters =
    search.trim() !== '' || categoryFilter !== 'all' || availabilityFilter !== 'all';

  const sectionSubtitle = vendorData.sections?.length
    ? ` · Sections: ${vendorData.sections.join(', ')}`
    : '';

  const selectedIds = Array.from(selectedItems);

  return (
    <div className={styles.menuManagement}>
      <div className={styles.header}>
        <button type="button" onClick={onBack} className={styles.back} title="Back to vendor list">
          <ArrowLeftIcon size={20} />
        </button>

        <div className={styles.headerContent}>
          <h1 className={styles.title}>{vendorData.name}</h1>
          <p className={styles.subtitle}>
            Menu management{sectionSubtitle}
          </p>
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            onClick={handleRefresh}
            className={styles.refreshButton}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>

          {menuItems.length > 0 && (
            <div className={styles.actionsMenuWrap} ref={actionsRef}>
              <button
                type="button"
                className={styles.actionsMenuButton}
                onClick={() => setActionsOpen((open) => !open)}
                aria-expanded={actionsOpen}
              >
                Menu actions
                <ArrowRightIcon size={14} className={styles.actionsCaret} />
              </button>
              {actionsOpen && (
                <div className={styles.actionsDropdown}>
                  <button
                    type="button"
                    className={styles.actionsDropdownItem}
                    onClick={exportMenu}
                    disabled={exporting}
                  >
                    <DownloadIcon size={16} />
                    {exporting ? 'Exporting…' : 'Export CSV'}
                  </button>
                  <button
                    type="button"
                    className={styles.actionsDropdownItem}
                    onClick={() => {
                      setActionsOpen(false);
                      setImportOpen(true);
                    }}
                  >
                    <UploadIcon size={16} />
                    Import CSV
                  </button>
                </div>
              )}
            </div>
          )}

          {menuItems.length > 0 && (
            <button type="button" onClick={onAddMenuItem} className={styles.addButton}>
              <PlusIcon size={20} />
              Add menu item
            </button>
          )}
        </div>
      </div>

      {!showSkeleton && menuItems.length > 0 && (
        <div className={styles.statTileRow}>
          <StatTile
            icon={<MenuIcon size={16} />}
            label="Total Items"
            value={menuItems.length}
          />
          <StatTile
            icon={<CheckIcon size={16} />}
            label="Available"
            value={availableCount}
            subtitle={unavailableCount > 0 ? `${unavailableCount} unavailable` : undefined}
            trendTone="success"
          />
          <StatTile
            icon={<DollarIcon size={16} />}
            label="Avg Price"
            value={avgPrice != null ? `$${avgPrice.toFixed(2)}` : '—'}
          />
          <StatTile
            icon={<TrendingUpIcon size={16} />}
            label="On Promotion"
            value={promoCount}
          />
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedIds.length} selected</span>
          <div className={styles.bulkBarActions}>
            <button
              type="button"
              className={styles.bulkBarSuccess}
              onClick={() => handleBulkAction('makeAvailable', selectedIds)}
              disabled={isBulkLoading || confirmLoading}
            >
              Mark available
            </button>
            <button
              type="button"
              className={styles.bulkBarMuted}
              onClick={() => handleBulkAction('makeUnavailable', selectedIds)}
              disabled={isBulkLoading || confirmLoading}
            >
              Mark unavailable
            </button>
            <button
              type="button"
              className={styles.bulkBarDanger}
              onClick={() => setPendingBulkDelete(true)}
              disabled={isBulkLoading || confirmLoading}
            >
              Delete selected
            </button>
          </div>
        </div>
      )}

      {showSkeleton ? (
        <div className={styles.grid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <MenuItemCardSkeleton key={i} />
          ))}
        </div>
      ) : menuItems.length === 0 ? (
        <div className={styles.empty}>
          <MenuIcon size={48} className={styles.emptyIconSvg} />
          <h3 className={styles.emptyTitle}>No menu items yet</h3>
          <p className={styles.emptyDescription}>
            Add your first item with photos, descriptions, and customization options.
          </p>
          <button type="button" onClick={onAddMenuItem} className={styles.emptyButton}>
            <PlusIcon size={20} />
            Add your first menu item
          </button>
        </div>
      ) : (
        <>
          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <SearchIcon size={18} className={styles.searchIcon} />
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Search menu items…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search menu items"
              />
            </div>

            <select
              className={styles.select}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filter by category"
              disabled={categories.length === 0}
            >
              <option value="all">
                {categories.length === 0 ? 'No categories yet' : 'All categories'}
              </option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {formatCategoryLabel(cat)}
                </option>
              ))}
            </select>

            <select
              className={styles.select}
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value as AvailabilityFilter)}
              aria-label="Filter by availability"
            >
              <option value="all">All items</option>
              <option value="available">Available only</option>
              <option value="unavailable">Unavailable only</option>
            </select>

            <select
              className={styles.select}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              aria-label="Sort menu items"
            >
              <option value="name-asc">Name A→Z</option>
              <option value="price-desc">Price high→low</option>
              <option value="price-asc">Price low→high</option>
            </select>
          </div>

          {filteredItems.length === 0 ? (
            <div className={styles.empty}>
              <SearchIcon size={48} className={styles.emptyIconSvg} />
              <h3 className={styles.emptyTitle}>No items match your filters</h3>
              <button type="button" onClick={clearFilters} className={styles.emptyButtonSecondary}>
                Clear filters
              </button>
            </div>
          ) : (
            <div className={styles.categorySections}>
              {itemsByCategory.map(([category, items]) => {
                const collapsed = collapsedCategories.has(category);
                return (
                  <section key={category} className={styles.categorySection}>
                    <button
                      type="button"
                      className={styles.categoryHeader}
                      onClick={() => toggleCategory(category)}
                      aria-expanded={!collapsed}
                    >
                      <ArrowRightIcon
                        size={16}
                        className={`${styles.categoryToggle} ${collapsed ? '' : styles.categoryToggleOpen}`}
                      />
                      <span className={styles.categoryTitle}>{formatCategoryLabel(category)}</span>
                      <span className={styles.categoryCount}>{items.length}</span>
                    </button>
                    {!collapsed && (
                      <div className={styles.grid}>
                        {items.map((item) => (
                          <MenuItemCard
                            key={item.id}
                            item={item}
                            selected={selectedItems.has(item.id)}
                            togglingId={togglingId}
                            onToggleSelect={handleItemSelect}
                            onEdit={onEditMenuItem}
                            onDelete={setPendingDelete}
                            onToggleAvailability={handleToggleAvailability}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}

          {hasFilters && filteredItems.length > 0 && (
            <p className={styles.resultCount}>
              Showing {filteredItems.length} of {menuItems.length} items
            </p>
          )}
        </>
      )}

      <Modal
        isOpen={importOpen}
        onClose={() => !isBulkLoading && setImportOpen(false)}
        title="Import menu items"
        size="lg"
        footer={
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.modalSecondary}
              onClick={() => setImportOpen(false)}
              disabled={isBulkLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.modalPrimary}
              onClick={importState.importItems}
              disabled={isBulkLoading || importState.previewCount === 0}
            >
              {isBulkLoading
                ? 'Importing…'
                : `Import ${importState.previewCount || ''} items`.trim()}
            </button>
          </div>
        }
      >
        <BulkMenuActions
          onBulkImport={handleBulkImport}
          isLoading={isBulkLoading}
          importOpen={importOpen}
          onImportOpenChange={setImportOpen}
          onImportStateChange={setImportState}
        />
      </Modal>

      <Modal
        isOpen={!!pendingDelete}
        onClose={() => !confirmLoading && setPendingDelete(null)}
        title="Delete menu item?"
        size="sm"
        footer={
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.modalSecondary}
              onClick={() => setPendingDelete(null)}
              disabled={confirmLoading}
            >
              Keep
            </button>
            <button
              type="button"
              className={styles.modalDanger}
              onClick={confirmSingleDelete}
              disabled={confirmLoading}
            >
              {confirmLoading ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        }
      >
        <p className={styles.confirmMessage}>
          Delete <strong>{pendingDelete?.name}</strong>? This cannot be undone.
        </p>
      </Modal>

      <Modal
        isOpen={pendingBulkDelete}
        onClose={() => !confirmLoading && setPendingBulkDelete(false)}
        title="Delete selected items?"
        size="sm"
        footer={
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.modalSecondary}
              onClick={() => setPendingBulkDelete(false)}
              disabled={confirmLoading}
            >
              Keep
            </button>
            <button
              type="button"
              className={styles.modalDanger}
              onClick={confirmBulkDelete}
              disabled={confirmLoading}
            >
              {confirmLoading ? 'Deleting…' : `Delete ${selectedIds.length} items`}
            </button>
          </div>
        }
      >
        <p className={styles.confirmMessage}>
          Permanently delete {selectedIds.length} selected menu item
          {selectedIds.length === 1 ? '' : 's'}? This cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default MenuManagement;
